import { useEffect, useState, type ReactNode } from "react";
import { colorFor } from '../lib/definitions/palette';
import { IN_FLIGHT_COLOR, MESHCORE_PACKET_COLOR, MESHCORE_PACKET_LABEL, MESHCORE_PACKET_ORDER, MESHTASTIC_PACKET_COLOR, MESHTASTIC_PACKET_LABEL, MESHTASTIC_PACKET_ORDER, normalizeMeshCorePacket, normalizeMeshtasticPacket, PROTOCOL_COLOR, PROTOCOL_LABEL, ROLE_CATEGORY_LABEL, ROLE_CATEGORY_ORDER, ROLE_SHADE, UNKNOWN_PROTOCOL_COLOR, UNKNOWN_PROTOCOL_LABEL, type MeshCorePacketCategory, type MeshtasticPacketCategory, type Protocol } from '../lib/definitions/protocols';
import { resolveNodeProtocol } from "../lib/nodeProtocol";
import { useAppData } from "../state/AppData";
import { useMapScopes } from "./map/useMapScopes";
import { useRegionReach } from "./map/useRegionReach";
import { reachKey, reachLegendText, REACH_CLEAR_LABEL, REACH_CONTROL_LABEL } from "../lib/regionReach";
import { repeaterScopeMarkers } from "../lib/liveMapData";
import { COLOUR_BY_OPTIONS, legendEntries, MESHTASTIC_NOT_COLOURED_NOTE, NON_REPEATER_NOTE, SCOPES_MESHCORE_ONLY_NOTE } from "../lib/mapScopes";
import { FIRST_CALCULATION_TEXT } from "../lib/definitions/analysis";
import { legendStorageKey, parseCollapsed, serializeCollapsed } from "../lib/legendPanel";
import { useIsPhone } from "../lib/useIsPhone";
import { MeshMapperLegendBody } from "./MeshMapperLegend";
import { useMeshMapperCoverage } from "./map/useMeshMapperCoverage";
import { MM_LAYER_TITLE } from "../lib/meshmapperCoverage";
import { FreqMapperLegendBody } from "./FreqMapperLegend";
import { useFreqMapperCoverage } from "./map/useFreqMapperCoverage";
import { FM_LAYER_TITLE } from "../lib/freqmapperCoverage";

// The map's legends, as two floating panels on the map itself (top left): "Node colour" (the Colour by switch, the
// scope list, the Region reach line and the node colours) and "Packet types". They follow the map's own colour
// scheme exactly: same logic, same names, one source (lib/definitions.ts): in ALL mode, two protocol swatches; in
// single-protocol mode, that protocol's four role-category swatches. Each panel folds up from its header; the choice
// is remembered per viewer (and separately at phone width, where they start folded).

/** One floating panel: a header that folds it, a body that scrolls when it is long. */
export function LegendPanel({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  const phone = useIsPhone();
  const key = legendStorageKey(id, phone);
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(key, phone));
  // The window can cross the phone breakpoint (rotation, resize): each width class has its own remembered choice.
  useEffect(() => setCollapsed(readCollapsed(key, phone)), [key, phone]);

  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(key, serializeCollapsed(next));
    } catch {
      /* private mode or blocked storage: the panel still folds, it just is not remembered */
    }
  }

  return (
    <section className="map-legend-panel" data-panel={id} data-collapsed={collapsed} aria-label={title}>
      <button type="button" className="map-legend-head" aria-expanded={!collapsed} onClick={toggle}>
        <span>{title}</span>
        <span className="map-legend-chevron" aria-hidden="true">
          {collapsed ? "▸" : "▾"}
        </span>
      </button>
      {!collapsed && <div className="map-legend-body">{children}</div>}
    </section>
  );
}

function readCollapsed(key: string, phone: boolean): boolean {
  try {
    return parseCollapsed(localStorage.getItem(key), phone);
  } catch {
    return phone; // blocked storage: the default for this width
  }
}

export function MapLegends() {
  return (
    <div className="map-legends">
      <LegendPanel id="node-colour" title="Node colour">
        <NodeColourLegend />
      </LegendPanel>
      <MeshMapperLegendPanel />
      <FreqMapperLegendPanel />
      <LegendPanel id="packet-types" title="Packet types">
        <PacketTypesLegend />
      </LegendPanel>
    </div>
  );
}

// The MeshMapper coverage panel: only there while Settings has the layer on (Settings, MeshMapper).
function MeshMapperLegendPanel() {
  const mm = useMeshMapperCoverage();
  if (!mm.available) return null;
  return (
    <LegendPanel id="meshmapper" title={MM_LAYER_TITLE}>
      <MeshMapperLegendBody mm={mm} />
    </LegendPanel>
  );
}

// The FreqMapper coverage panel: only there while Settings has the layer on (Settings, FreqMapper).
function FreqMapperLegendPanel() {
  const fm = useFreqMapperCoverage();
  if (!fm.available) return null;
  return (
    <LegendPanel id="freqmapper" title={FM_LAYER_TITLE}>
      <FreqMapperLegendBody fm={fm} />
    </LegendPanel>
  );
}

function NodeColourLegend() {
  const { effectiveTheme, nodes, protocolFilter } = useAppData();

  const { colourBy, scopesAvailable, setColourBy, view: scopeView, colours: scopeColours, pending: scopePending } = useMapScopes();
  const reach = useRegionReach();
  const scopeRows = scopeView ? legendEntries(repeaterScopeMarkers(nodes, protocolFilter, scopeView), scopeView) : [];

  let unknownProtocolCount = 0;
  for (const n of nodes.values()) {
    if (resolveNodeProtocol(n) === "unknown") unknownProtocolCount += 1;
  }

  return (
    <div className="legend">
      <div className="legend-section">
        <h4>Colour by</h4>
        <div className="theme-toggle" role="group" aria-label="Colour by">
          {COLOUR_BY_OPTIONS.map((o) => (
            <button key={o.id} type="button" data-active={colourBy === o.id} disabled={o.id === "scope" && !scopesAvailable} onClick={() => setColourBy(o.id)}>
              {o.label}
            </button>
          ))}
        </div>
        {!scopesAvailable && <div className="legend-note">{SCOPES_MESHCORE_ONLY_NOTE}</div>}
      </div>

      {colourBy === "scope" && (
        <div className="legend-section">
          <h4>Repeater scope</h4>
          {scopePending && <div className="legend-row">{FIRST_CALCULATION_TEXT}</div>}
          <div className="legend-grid">
            {scopeRows.map((r) => {
              const pickable = !r.key.startsWith("__");
              return pickable ? (
                <button
                  type="button"
                  className="legend-row legend-row-button"
                  key={r.key}
                  aria-pressed={reach.selected === reachKey(r.label)}
                  title={`Show where ${r.label} was heard (${REACH_CONTROL_LABEL})`}
                  onClick={() => reach.select(reach.selected === reachKey(r.label) ? null : r.label)}
                >
                  <span className="legend-swatch" style={{ background: r.colour }} />
                  {r.label} ({r.count})
                </button>
              ) : (
                <div className="legend-row" key={r.key}>
                  <span className="legend-swatch" style={{ background: r.colour }} />
                  {r.label} ({r.count})
                </div>
              );
            })}
          </div>
          <div className="legend-note">{MESHTASTIC_NOT_COLOURED_NOTE}</div>
          <div className="legend-note">{NON_REPEATER_NOTE}</div>
        </div>
      )}

      {reach.selected !== null && (
        <div className="legend-section" data-testid="reach-legend">
          <h4>{REACH_CONTROL_LABEL}</h4>
          {reach.scope ? (
            <>
              <div className="legend-row">
                <span
                  className="legend-swatch"
                  style={{ background: scopeColours?.scopes.find((x) => reachKey(x.name) === reach.selected)?.colour ?? "#888" }}
                />
                {reachLegendText(reach.scope.name, reach.scope)}
              </div>
            </>
          ) : (
            <div className="legend-note">{reach.pending ? FIRST_CALCULATION_TEXT : "Nothing heard for this scope in 7 d"}</div>
          )}
          <button type="button" className="rf-link-btn" onClick={() => reach.select(null)}>
            {REACH_CLEAR_LABEL}
          </button>
        </div>
      )}

      <div className="legend-section">
        <h4>Node colour</h4>
        {(["meshtastic", "meshcore"] as const)
          .filter((p) => protocolFilter === "all" || protocolFilter === p)
          .map((p) => (
            <div className="legend-group" key={p}>
              <div className="legend-subhead">{PROTOCOL_LABEL[p]}</div>
              {ROLE_CATEGORY_ORDER.map((cat) => (
                <div className="legend-row" key={cat}>
                  <span
                    className={p === "meshtastic" && cat === "router_repeater" ? "legend-swatch legend-swatch-big" : "legend-swatch"}
                    style={{ background: colorFor(ROLE_SHADE[p][cat], effectiveTheme) }}
                  />
                  {ROLE_CATEGORY_LABEL[p][cat]}
                </div>
              ))}
            </div>
          ))}
        {unknownProtocolCount > 0 && (
          <div className="legend-row">
            <span className="legend-swatch" style={{ background: colorFor(UNKNOWN_PROTOCOL_COLOR, effectiveTheme) }} />
            {UNKNOWN_PROTOCOL_LABEL} ({unknownProtocolCount})
          </div>
        )}
      </div>

    </div>
  );
}

function PacketTypesLegend() {
  const { effectiveTheme, packets, protocolFilter } = useAppData();

  const meshtasticPacketsSeen = new Set<MeshtasticPacketCategory>();
  const meshcorePacketsSeen = new Set<MeshCorePacketCategory>();
  for (const p of packets) {
    if (p.protocol === "meshtastic") meshtasticPacketsSeen.add(normalizeMeshtasticPacket(p.type));
    else if (p.protocol === "meshcore") meshcorePacketsSeen.add(normalizeMeshCorePacket(p.type));
  }

  const showMeshtasticPackets = meshtasticPacketsSeen.size > 0 && (protocolFilter === "all" || protocolFilter === "meshtastic");
  const showMeshcorePackets = meshcorePacketsSeen.size > 0 && (protocolFilter === "all" || protocolFilter === "meshcore");

  return (
    <div className="legend">
      {showMeshtasticPackets && (
        <PacketSection
          protocol="meshtastic"
          order={MESHTASTIC_PACKET_ORDER}
          label={MESHTASTIC_PACKET_LABEL}
          color={MESHTASTIC_PACKET_COLOR}
          seen={meshtasticPacketsSeen}
          effectiveTheme={effectiveTheme}
        />
      )}
      {showMeshcorePackets && (
        <PacketSection
          protocol="meshcore"
          order={MESHCORE_PACKET_ORDER}
          label={MESHCORE_PACKET_LABEL}
          color={MESHCORE_PACKET_COLOR}
          seen={meshcorePacketsSeen}
          effectiveTheme={effectiveTheme}
        />
      )}
      {!showMeshtasticPackets && !showMeshcorePackets && (
        <div className="legend-section" style={{ color: "var(--ink-muted)" }}>
          No packets observed yet for the current protocol filter.
        </div>
      )}

      <div className="legend-section">
        <h4>Live packets</h4>
        <div className="legend-row">
          <span className="legend-swatch" style={{ background: IN_FLIGHT_COLOR }} />
          Pulse — a packet just transmitted, every packet gets one
        </div>
        <div className="legend-row">
          <span className="legend-swatch" style={{ background: IN_FLIGHT_COLOR }} />
          Ray — reception path (where we heard it), not the packet's destination
        </div>
      </div>
    </div>
  );
}

function PacketSection<P extends string>(props: {
  protocol: Protocol;
  order: P[];
  label: Record<P, string>;
  color: Record<P, { light: string; dark: string }>;
  seen: Set<P>;
  effectiveTheme: "light" | "dark";
}) {
  const { protocol, order, label, color, seen, effectiveTheme } = props;
  return (
    <div className="legend-section">
      <h4>{PROTOCOL_LABEL[protocol]} packet types (observed)</h4>
      {order
        .filter((c) => seen.has(c))
        .map((c) => (
          <div className="legend-row" key={c}>
            <span className="legend-swatch" style={{ background: colorFor(color[c], effectiveTheme) }} />
            {label[c]}
          </div>
        ))}
      {seen.has("other" as P) && (
        <div className="legend-row">
          <span className="legend-swatch" style={{ background: colorFor(color["other" as P], effectiveTheme) }} />
          {label["other" as P]}
        </div>
      )}
    </div>
  );
}
